import React, { ReactNode, useCallback, useEffect } from "react";
import classnames from "classnames";

import { Boxes } from "@formant/ui-kit";
import { cn } from "@formant/ui-kit/src/lib/utils";
import { Icon, IconName } from "@formant/ui/component/common/Icon";

import { useStores } from "../../context/StoreContext";
import styles from "./Header.scss";
import { Heading1 } from "./Heading1";
import { Heading3 } from "./Heading3";
import { IconButton } from "./IconButton";

export interface IHeaderProps {
    title: string;
    hideTitle?: boolean;
    hideMenu?: boolean;
    className?: string;
    backTooltip?: string;
    preIcon?: ReactNode;
    preTitle?: ReactNode;
    subTitle?: string;
    onBack?: () => void;
    onHeaderClick?: () => void;
    onAction?: () => void;
    onCustomMenuAction?: () => void;
    actionIcon?: IconName;
    actionTooltip?: string;
    children?: React.ReactNode;
    setDocumentTitle?: boolean;
    showDropdownIcon?: boolean;
    isEditMode?: boolean;
}

export function Header(props: IHeaderProps) {
    const {
        title,
        hideTitle,
        hideMenu,
        className,
        backTooltip,
        preIcon,
        preTitle,
        subTitle,
        onBack,
        children,
        onAction,
        actionIcon,
        actionTooltip,
        onHeaderClick,
        onCustomMenuAction,
        setDocumentTitle = true,
        showDropdownIcon = true,
        isEditMode = false,
    } = props;

    const showBackButton = !!onBack;

    useEffect(() => {
        if (setDocumentTitle) {
            document.title = `Formant${title ? `: ${title}` : ""}`;
        }
    }, [title]);

    const { appStore } = useStores();

    const handleOnMenu = useCallback(() => {
        appStore.sidebarIsOpen = true;
    }, [appStore]);

    const withSubtitle = (
        <div
            className={cn(
                "flex flex-col bg-formant-color-background-primary rounded-lg pb-[0.35rem] pt-[0.25rem] pr-[1rem] pl-[0.5rem]",
                showDropdownIcon &&
                    "hover:bg-formant-color-background-active-2 transition-colors",
            )}
        >
            <div className="flex items-center gap-2">
                <Boxes size="24" strokeWidth={1} className="mb-auto mt-[4px]" />
                <div className="flex flex-col">
                    <Heading1
                        className={cn(
                            "text-formant-white",
                            showDropdownIcon &&
                                "hover:text-formant-white/90 transition-colors",
                            actionIcon && "w-full",
                            onHeaderClick && showDropdownIcon
                                ? "cursor-pointer"
                                : "cursor-default",
                        )}
                        data-testid="heading-title-with-subtitle"
                        onClick={onHeaderClick}
                        data-tooltip={title}
                    >
                        {title}
                    </Heading1>
                    <div
                        className={cn(
                            "flex items-center gap-1 text-formant-silver whitespace-nowrap",
                            showDropdownIcon &&
                                "hover:text-formant-silver/90 transition-colors",
                            onHeaderClick && showDropdownIcon
                                ? "cursor-pointer"
                                : "cursor-default",
                        )}
                        onClick={onHeaderClick}
                    >
                        <Heading3 className={cn(actionIcon && "w-full")}>
                            {subTitle}
                        </Heading3>
                        {showDropdownIcon && (
                            <Icon
                                name="dropdown"
                                size="17"
                                className="-ml-[5px] mt-[1px]"
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    const withDropdown = (
        <>
            <Heading1
                className={classnames(
                    actionIcon && styles["heading-text-full-width"],
                    showDropdownIcon ? styles.clickable : "cursor-default",
                )}
                data-testid="heading-title-with-dropdown"
                onClick={onHeaderClick}
                data-tooltip={title}
            >
                {title}
            </Heading1>

            {showDropdownIcon && (
                <span onClick={onHeaderClick}>
                    <Icon name="dropdown" size="24" />
                </span>
            )}
        </>
    );

    const withoutDropdown = (
        <Heading1
            className={actionIcon && styles["heading-text-full-width"]}
            data-testid="heading-title-without-dropdown"
            data-tooltip={title}
        >
            {title}
        </Heading1>
    );

    return (
        <div
            className={classnames(
                styles.header,
                subTitle && styles["with-subtitle"],
                isEditMode && styles["edit-mode"],
                className,
            )}
        >
            {preIcon}
            {!showBackButton && !hideMenu && (
                <IconButton
                    data-testid="menu-button"
                    icon="menu"
                    onClicked={handleOnMenu}
                    className={classnames(styles.menu, "menu")}
                />
            )}
            {onCustomMenuAction && (
                <IconButton
                    data-testid="menu-button"
                    icon="menu"
                    onClicked={onCustomMenuAction}
                    className={classnames(styles.menu, "menu")}
                />
            )}
            {showBackButton && (
                <IconButton
                    data-testid="back-button"
                    icon="arrow-left"
                    onClicked={onBack}
                    className={styles.back}
                    data-tooltip={backTooltip}
                />
            )}
            {preTitle}
            {!hideTitle &&
                !subTitle &&
                (onHeaderClick ? withDropdown : withoutDropdown)}
            {!hideTitle && subTitle && onHeaderClick && withSubtitle}
            {children}
            {actionIcon && (
                <IconButton
                    icon={actionIcon}
                    onClicked={onAction}
                    className={styles.action}
                    data-tooltip={actionTooltip}
                />
            )}
        </div>
    );
}
